import React, { useMemo } from 'react';
import SubHeader from '~/pages/templates/SubHeader';
import { DropDown } from '~/components';
import { useDashboard } from './context';
import { MdArrowDropDownCircle } from 'react-icons/md';
import { FaRegCalendar } from 'react-icons/fa';
import { asyncState } from '~/hooks';
import { isEmpty } from 'lodash';

const Header = () => {
    const { timespan, setTimespan, userMetrics, tryonMetrics, quickStatusFilter } = useDashboard();

    return (
        <SubHeader
            className='h-44 py-4'
            title='Recent Activity'
            subtitle={() => <TimespanSwitch timespan={timespan} updateTimespan={setTimespan} />}
            content={() => <Metrics userMetrics={userMetrics} tryonMetrics={tryonMetrics} quickStatusFilter={quickStatusFilter}/>}/>
    );
};

export default Header;

const timespanText = Object.freeze({
    [1]: 'today',
    [7]: 'the last 7 days',
    [30]: 'the last 30 days',
    [180]: 'the last 6 months',
    [365]: 'the last year',
    [0]: 'all time'
});

function TimespanSwitch({timespan, updateTimespan}) {
    const text = `For ${timespanText[timespan]}`;

    return (
        <div className='flex flex-col justify-between w-1/2' style={{ minWidth: 'fit-content' }}>
            <p className='text-sm font-bold text-neutral-80'>{text}</p>
            <DropDown 
                text='Switch' 
                wrapperClass="bg-white rounded-md shadow-md"
                icon={MdArrowDropDownCircle}
                iconSize='1.375em'
                iconClass='ml-1'
                buttonClass='mt-2 pb-2 text-blue-50 font-semibold text-sm focus:outline-none flex items-center justify-center'>
                <div className="flex flex-col items-stretch w-40 text-neutral-80 text-sm">
                    <p className="font-semibold p-4 py-2">Switch View</p>
                    <button 
                        className="py-2 px-4 hover:bg-blue-10 focus:outline-none flex items-center"
                        onClick={() => updateTimespan(365)}>
                        <CalendarIcon>365</CalendarIcon>
                        <span className="ml-2 pt-1">Last Year</span>
                    </button>
                    <button 
                        className="py-2 px-4 hover:bg-blue-10 focus:outline-none flex items-center"
                        onClick={() => updateTimespan(180)}>
                        <CalendarIcon>180</CalendarIcon>
                        <span className="ml-2 pt-1">Last 6 Months</span>
                    </button>
                    <button 
                        className="py-2 px-4 hover:bg-blue-10 focus:outline-none flex items-center"
                        onClick={() => updateTimespan(30)}>
                        <CalendarIcon>30</CalendarIcon>
                        {/* <span className='text-xs tracking-tighter font-medium'>30</span> */}
                        <span className="ml-2 pt-1 ">Last 30 Days</span>
                    </button>
                    <button 
                        className="py-2 px-4 hover:bg-blue-10 focus:outline-none flex items-center"
                        onClick={() => updateTimespan(7)}>
                        <CalendarIcon>7</CalendarIcon>
                        <span className="ml-2 pt-1 ">Last 7 Days</span>
                    </button>
                    <button 
                        className="py-2 px-4 hover:bg-blue-10 focus:outline-none flex items-center"
                        onClick={() => updateTimespan(1)}>
                        <CalendarIcon>1</CalendarIcon>
                        <span className="ml-2 pt-1 ">Today</span>
                    </button>
                    <button 
                        className="py-2 px-4 border-t hover:bg-blue-10 focus:outline-none flex items-center"
                        onClick={() => updateTimespan(0)}>
                        <CalendarIcon>All</CalendarIcon>
                        <span className="ml-2 pt-1 pb-1 ">All Time</span>
                    </button>
                </div>
            </DropDown>
        </div>
    );
}

function CalendarIcon ({children}) {
    return (
        <span className='relative h-4 w-4 mr-1 mb-2 mt-1 text-neutral-80'>
            <FaRegCalendar 
                className='absolute top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2'
                size='1.825em'/>
            <span className='z-10 text-xs font-medium tracking-tighter absolute top-3/4 left-1/2 transform -translate-y-1/2 -translate-x-1/2 scale-75'>
                {children}
            </span>
        </span>
    );
}

const defaultMetrics = {
    found: 0,
    completed: 0,
    foundRatio: 0.0,
    turnaround: 0,
    turnMin: 0,
    turnSec: 0
};

function Metrics ({ userMetrics, tryonMetrics }) {
    const { userTimeSpan, setUserTimeSpan } = useDashboard();

    const userData = useMemo(() => {
        if (userMetrics.status == asyncState.SUCCESS && !isEmpty(userMetrics.value)) {
            const {
                averageTurnaroundSeconds: turnaround,
                modified: values
            } = userMetrics.value;
            
            return { 
                turnaround: turnaround || 0,
                turnMin: turnaround > 0 ? Math.min(999, Math.floor(turnaround / 60)) : 0,
                turnSec: turnaround > 0 ? turnaround % 60 : 0,
                found: values.found,
                completed: (values.found + values.notFound),
                foundRatio: Math.round(100 * (values.found / (values.found + values.notFound))) || 0
            };
        }

        return defaultMetrics;
    }, [userMetrics.status]);

    const tryonData = useMemo(() => {
        if (tryonMetrics.status == asyncState.SUCCESS && !isEmpty(tryonMetrics.value)) {
            const {
                averageTurnaroundSeconds: turnaround,
                ...values
            } = tryonMetrics.value;
            
            return { 
                turnaround: turnaround || 0,
                turnMin: turnaround > 0 ? Math.min(999, Math.floor(turnaround / 60)) : 0,
                turnSec: turnaround > 0 ? turnaround % 60 : 0,
                found: values.found,
                completed: (values.found + values.notFound),
                foundRatio: Math.round(100 * (values.found / (values.found + values.notFound))) || 0
            };
        }

        return defaultMetrics;
    }, [tryonMetrics.status]);

    return (
        <div className='my-auto grid grid-flow-row'>
            <Row className='p-2 pb-0 border-b border-gray-290'>
                <Column>
                    <span className='font-bold text-secondary-100 mb-auto'>Your<br/>Activity</span>
                    <TimespanSwitch timespan={userTimeSpan} updateTimespan={setUserTimeSpan} />
                </Column>
                <Column>
                    <MetricLabel>Prior Policies<br/>Found</MetricLabel>
                    <MetricValue>{userData.found}</MetricValue>
                </Column>
                <Column>
                    <MetricLabel>Prior Policies<br/>Completed</MetricLabel>
                    <MetricValue>{userData.completed}</MetricValue>
                </Column>
                <Column>
                    <MetricLabel>Prior Policies<br/>Found %</MetricLabel>
                    <MetricValue>{userData.foundRatio}%</MetricValue>
                </Column>
                <Column>
                    <MetricLabel>Avg. Turn<br/>Time</MetricLabel>
                    <MetricValue>
                        {userData.turnMin}<small className='text-base tracking-normal'>min</small>&nbsp;
                        {userData.turnSec}<small className='text-base'>sec</small>
                    </MetricValue>
                </Column>
            </Row>
            <Row className='p-2 pt-1'>
                <span className='font-bold text-secondary-100'>Tryon</span>
                <Column>
                    <MetricValue color='primary-80' size='base'>{tryonData.found}</MetricValue>
                </Column>
                <Column>
                    <MetricValue color='primary-80' size='base'>{tryonData.completed}</MetricValue>
                </Column>
                <Column>
                    <MetricValue color='primary-80' size='base'>{tryonData.foundRatio}%</MetricValue>
                </Column>
                <Column>
                    <MetricValue color='primary-80' size='base'>
                        {tryonData.turnMin}<small className='text-base tracking-normal'>min</small>&nbsp;
                        {tryonData.turnSec}<small className='text-base'>sec</small>
                    </MetricValue>
                </Column>
            </Row>
        </div>
    );
}

function MetricValue ({children, size='2xl', color='primary-50'}) {
    return (
        <span className={`text-${size} tracking-tight font-semibold text-${color}`}>
            {children}
        </span>
    );
}

function MetricLabel ({children}) {
    return (
        <span className='h-10'>
            <p className='text-xs font-medium text-neutral-80 uppercase'>{children}</p>
        </span>
    );
}

function Row ({ children, className }) {
    return (
        <div className={`${className} grid grid-flow-col auto-cols-fr`}>
            {children}
        </div>
    );
}

function Column ({ children, className }) {
    return (
        <div className={`${className} grid grid-flow-row`}>
            {children}
        </div>
    );
}